import {Autocomplete, Box, Select, type SelectItem} from '@mantine/core';
import type React from 'react';
import type {FC} from 'react';
import {MdSearch} from 'react-icons/md';
import {
  COLOR_BACKGROUND_1,
  COLOR_BLACK,
  COLOR_PRIMARY,
} from '../../../../../config/constants/colorCodes';
import {FILTER_EMPLOYEE_COUNT_OPTIONS} from '../../../../../core/entities/company/constants';
import {CommonLink} from '../../../../../shared/components/CommonLink';
import {useMediaQuery} from '../../../../../shared/services/useMediaQuery';
import {MultiSelectMenu} from './MultiSelectMenu';

type Props = {
  searchWord: string;
  companies: Array<{id: number; name: string}>;
  handleSearchInputChange: (searchWord: string) => void;
  handleFilterEmployeeCountChange: (optionIndex: number) => void;
  attractiveConditionsList: SelectItem[];
  handleFilterAttractiveConditionChange: (filterValues: string[]) => void;
  filterAttractiveConditions: string[];
};

type AutoCoumpleteData = {
  id: string;
  value: string;
  url: string;
};

// TODO: ドロップダウンの幅調整

const CompanyNameSearchInput: FC<{
  searchWord: string;
  autoCompleteData: AutoCoumpleteData[];
  isPC: boolean;
  handleSearchInputChange: (searchWord: string) => void;
}> = ({searchWord, autoCompleteData, isPC, handleSearchInputChange}) => {
  return (
    <Autocomplete
      placeholder={'企業名で検索'}
      icon={<MdSearch size="28px" />}
      itemComponent={(props) => (
        <CommonLink
          key={`company-${props.id}`}
          href={props.url}
          className="border-b-2 border-line cursor-pointer flex items-center line-clamp-2 hover:underline text-primary p-4"
        >
          {props.value}
        </CommonLink>
      )}
      data={autoCompleteData}
      value={searchWord}
      onChange={(searchWord) => {
        handleSearchInputChange(searchWord);
      }}
      size="sm"
      dropdownPosition="bottom"
      limit={1000}
      maxDropdownHeight={320}
      className="w-full"
      styles={{
        input: {
          backgroundColor: COLOR_BACKGROUND_1,
          border: 'none',
          borderRadius: '20px',
          '::placeholder': {
            color: COLOR_BLACK,
          },
          height: isPC ? 40 : 36,
        },
        icon: {
          color: 'black',
        },
      }}
    />
  );
};

export const CompanyFilter: React.FC<Props> = ({
  searchWord,
  companies,
  handleSearchInputChange,
  handleFilterEmployeeCountChange,
  attractiveConditionsList,
  handleFilterAttractiveConditionChange,
  filterAttractiveConditions,
}) => {
  const autoCompleteData = companies.map((company) => ({
    id: String(company.id),
    value: company.name,
    url: `/company/${company.id}`,
  }));

  const convertedFilterEmployeeCountOptions: SelectItem[] =
    FILTER_EMPLOYEE_COUNT_OPTIONS.map((option, index) => ({
      value: index.toString(),
      label: option.label,
    }));

  const {isPC} = useMediaQuery();

  const handleAttractiveConditionsChange = (selectedValues: string[]) => {
    handleFilterAttractiveConditionChange(selectedValues);
  };

  return (
    <Box className="mb-8 flex items-center justify-start gap-x-12 px-16 py-12 pc:shadow-card pc:my-8 pc:rounded-16 pc:bg-white flex-col pc:flex-row">
      <Box className="flex items-center gap-8  justify-between w-full pc:w-auto">
        <Box className="flex gap-8">
          <MultiSelectMenu
            title="こだわり条件"
            items={attractiveConditionsList}
            onChangeMenu={handleAttractiveConditionsChange}
            value={filterAttractiveConditions}
            position="bottom-start"
          />
        </Box>
        <Box className="flex gap-8">
          <Select
            clearable
            data={convertedFilterEmployeeCountOptions}
            placeholder="従業員数"
            className="w-[120px] pc:w-[200px]"
            styles={{
              input: {
                backgroundColor: isPC ? COLOR_BACKGROUND_1 : '',
                border: 'none',
                borderRadius: '20px',
                height: 40,
                '::placeholder': {
                  fontWeight: 'bold',
                  textAlign: 'center',
                  color: isPC ? COLOR_PRIMARY : COLOR_BLACK,
                },
              },
              icon: {
                color: COLOR_PRIMARY,
              },
            }}
            onChange={(value: string) => {
              if (value === null) {
                handleFilterEmployeeCountChange(Number(-1));
                return;
              }

              handleFilterEmployeeCountChange(Number(value));
            }}
          />
        </Box>
      </Box>
      <Box className="w-full pc:w-[420px]">
        <CompanyNameSearchInput
          searchWord={searchWord}
          autoCompleteData={autoCompleteData}
          isPC={true}
          handleSearchInputChange={handleSearchInputChange}
        />
      </Box>
    </Box>
  );
};
